import { useEffect, useMemo, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import type { Permission } from "@platform/core";
import { Alert, Button, Card, Spinner, ToastProvider, useToast } from "@platform/ui";
import { createApiClient } from "./api/client";
import { apiErrorBus } from "./api/error-bus";
import { isApiError } from "./api/errors";
import type { ConsoleAuth } from "./auth";
import type { RuntimeConfig } from "./config";
import { ConsoleProvider, type ConsoleContextValue } from "./context";
import { currentLocale, useT } from "./i18n";
import { errorText } from "./lib/errors";
import { LoginPage } from "./pages/LoginPage";
import { createAppRouter } from "./routes";

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 text-center">{children}</Card>
    </div>
  );
}

export function Splash({ label }: { label: string }) {
  return (
    <div
      className="flex min-h-screen items-center justify-center gap-3 text-ink-2"
      role="status"
      aria-live="polite"
    >
      <Spinner />
      {label}
    </div>
  );
}

/** Routes global API errors to toasts / re-login. */
function ErrorBridge({ auth }: { auth: ConsoleAuth }) {
  const toast = useToast();
  const { t } = useT();
  useEffect(
    () =>
      apiErrorBus.subscribe((e) => {
        if (e.status === 401) {
          // Session gone (token expired and silent renewal failed): back to the console's own login page.
          void auth.clear().then(() => window.location.assign(loginPath("expired")));
        } else if (e.status === 403)
          toast({ title: t("error.forbidden"), description: e.detail, tone: "error" });
        else if (e.status === 429)
          toast({ title: t("error.rateLimited", { seconds: e.retryAfter ?? 60 }), tone: "error" });
        else if (e.status === 0) toast({ title: t("error.network"), tone: "error" });
      }),
    [auth, toast, t],
  );
  return null;
}

function Authenticated({ config, auth }: { config: RuntimeConfig; auth: ConsoleAuth }) {
  const { t } = useT();
  const api = useMemo(
    () =>
      createApiClient({
        baseUrl: config.authServerUrl,
        getToken: () => auth.accessToken(),
        getLocale: currentLocale,
        onError: (e) => apiErrorBus.emit(e),
      }),
    [config, auth],
  );
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: 1 });
  const branding = useQuery({ queryKey: ["branding"], queryFn: api.publicBranding, staleTime: 5 * 60_000 });
  const router = useMemo(() => createAppRouter(), []);

  if (me.isPending) return <Splash label={t("common.loading")} />;
  if (me.isError) {
    return (
      <Centered>
        <Alert tone="error" title={t("common.errorTitle")}>
          {errorText(me.error)}
        </Alert>
        <Button className="mt-4" onClick={() => void me.refetch()}>
          {t("common.retry")}
        </Button>
      </Centered>
    );
  }
  if (!me.data.consoleAccess) {
    return (
      <Centered>
        <h1 className="mb-2 text-xl font-medium">{t("auth.noAccessTitle")}</h1>
        <p className="mb-6 text-sm text-ink-2">{t("auth.noAccessBody", { email: me.data.user.email })}</p>
        <Button onClick={() => void auth.signOut()}>{t("header.signOut")}</Button>
      </Centered>
    );
  }
  const permissions = new Set<Permission>(me.data.permissions);
  const value: ConsoleContextValue = {
    config,
    auth,
    api,
    me: me.data,
    productName: branding.data?.productName ?? t("app.name"),
    can: (p) => permissions.has(p),
  };
  return (
    <ConsoleProvider value={value}>
      <RouterProvider router={router} />
    </ConsoleProvider>
  );
}

const LOGIN_PATH = "/login";

/** Same-origin path to come back to after sign-in (never the login or callback page itself). */
function safeReturnTo(value: string | null | undefined): string {
  return value &&
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.startsWith(LOGIN_PATH) &&
    !value.startsWith("/auth/callback")
    ? value
    : "/";
}

/** /login?returnTo=<current page> — the console's own sign-in page. */
function loginPath(reason?: "expired"): string {
  const params = new URLSearchParams();
  if (reason) params.set("reason", reason);
  const here = safeReturnTo(window.location.pathname + window.location.search);
  if (here !== "/") params.set("returnTo", here);
  const q = params.toString();
  return q ? `${LOGIN_PATH}?${q}` : LOGIN_PATH;
}

type BootResult = "ready" | "signedOut";

/** Runs once per page load (StrictMode double-invokes effects; the OIDC callback can be consumed once). */
let bootstrap: Promise<BootResult> | null = null;
function bootstrapAuth(auth: ConsoleAuth): Promise<BootResult> {
  bootstrap ??= (async () => {
    if (window.location.pathname === "/auth/callback") {
      const back = await auth.completeSignIn();
      window.history.replaceState(null, "", back);
    }
    const user = await auth.user();
    const token = user ? await auth.accessToken() : null;
    const onLogin = window.location.pathname === LOGIN_PATH;
    if (!token) {
      // No automatic redirect to the auth server: show the console's login page and wait for a click.
      if (!onLogin) window.history.replaceState(null, "", loginPath());
      return "signedOut" as const;
    }
    if (onLogin) {
      // Already signed in (e.g. bookmark of /login): go where the page asked for.
      const rt = new URLSearchParams(window.location.search).get("returnTo");
      window.history.replaceState(null, "", safeReturnTo(rt));
    }
    return "ready" as const;
  })();
  return bootstrap;
}

type Phase =
  { kind: "loading" } | { kind: "ready" } | { kind: "signedOut" } | { kind: "error"; message: string };

export function App({ config, auth }: { config: RuntimeConfig; auth: ConsoleAuth }) {
  const { t } = useT();
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 15_000,
            refetchOnWindowFocus: false,
            retry: (n, e) => !isApiError(e) || (e.status >= 500 && n < 2),
          },
          mutations: { retry: false },
        },
      }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await bootstrapAuth(auth);
        if (!cancelled) setPhase({ kind: r });
      } catch (e) {
        if (!cancelled) setPhase({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  if (phase.kind === "error") {
    return (
      <Centered>
        <h1 className="mb-2 text-xl font-medium">{t("auth.failed")}</h1>
        <p className="mb-6 text-sm break-words text-ink-2">{phase.message}</p>
        <Button
          onClick={() => {
            bootstrap = null;
            window.history.replaceState(null, "", "/");
            void auth.signIn("/");
          }}
        >
          {t("auth.tryAgain")}
        </Button>
      </Centered>
    );
  }
  if (phase.kind === "signedOut") {
    const q = new URLSearchParams(window.location.search);
    const reason = q.get("reason");
    return (
      <LoginPage
        auth={auth}
        returnTo={safeReturnTo(q.get("returnTo"))}
        reason={reason === "expired" || reason === "signedOut" ? reason : null}
      />
    );
  }
  if (phase.kind === "loading")
    return (
      <Splash
        label={window.location.pathname === "/auth/callback" ? t("auth.callback") : t("auth.redirecting")}
      />
    );
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ErrorBridge auth={auth} />
        <Authenticated config={config} auth={auth} />
      </ToastProvider>
    </QueryClientProvider>
  );
}
